import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Card, Typography, Space, Divider, Alert } from 'antd';
import {
  DashboardOutlined,
  RobotOutlined,
  HeatMapOutlined,
  GlobalOutlined,
  BarChartOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  RightOutlined,
  LeftOutlined,
  SettingOutlined,
  UserOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const AppWalkthrough = ({ visible, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState(null);

  const walkthroughSteps = useMemo(() => ([
    {
      title: "Welcome to Asuma!",
      description: "Let's take a guided tour of your analytics dashboard",
      icon: <CheckCircleOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      highlightSelector: null,
      content: (
        <div style={{ textAlign: 'center' }}>
          <Paragraph>
            We'll walk through each feature in your sidebar and show you exactly how to use them.
            This interactive tour will highlight each button as we go.
          </Paragraph>
          <Space direction="vertical" style={{ marginTop: 16 }}>
            <Text strong>🎯 Interactive highlights</Text>
            <Text strong>📋 Step-by-step instructions</Text>
            <Text strong>💡 Practical examples</Text>
            <Text strong>⚡ Quick setup guides</Text>
          </Space>
        </div>
      )
    },
    {
      title: "Dashboard - Your Analytics Hub",
      description: "Click the Dashboard button to see your main analytics overview",
      icon: <DashboardOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      highlightSelector: 'a[title="Dashboard"]',
      content: (
        <div>
          <Paragraph>
            Your dashboard shows real-time metrics and insights at a glance:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Website Status:</strong> Active/inactive sites and health indicators</Text>
            <Text>• <strong>Live Visitors:</strong> Current visitor count and trends</Text>
            <Text>• <strong>Event Summary:</strong> Pageviews and clicks from the last 7 days</Text>
            <Text>• <strong>Traffic Overview:</strong> Daily activity charts and patterns</Text>
            <Text>• <strong>Engagement Metrics:</strong> User interaction data and funnels</Text>
          </Space>
          <Alert
            message="💡 Quick Tip"
            description="The dashboard auto-refreshes every 30 seconds to show live data"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      )
    },
    {
      title: "AI Copilot - Smart Analytics Assistant",
      description: "Click the AI Chat button to ask questions about your data",
      icon: <RobotOutlined style={{ fontSize: 48, color: '#722ed1' }} />,
      highlightSelector: 'a[title="AI Chat"]',
      content: (
        <div>
          <Paragraph>
            Ask natural language questions about your website performance:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Automated Analysis:</strong> "Why is my bounce rate high?"</Text>
            <Text>• <strong>Performance Insights:</strong> "Which pages perform best?"</Text>
            <Text>• <strong>Recommendations:</strong> "Suggest A/B tests for conversion"</Text>
            <Text>• <strong>Issue Detection:</strong> "Find pages with high exit rates"</Text>
            <Text>• <strong>Trend Analysis:</strong> "Show traffic patterns this week"</Text>
          </Space>
          <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
            <Text strong style={{ color: '#52c41a' }}>🚀 Enterprise Feature: Requires premium plan</Text>
          </div>
        </div>
      )
    },
    {
      title: "Websites & Snippets - Setup Tracking",
      description: "Click Websites & Snippets to manage your tracked sites",
      icon: <GlobalOutlined style={{ fontSize: 48, color: '#13c2c2' }} />,
      highlightSelector: 'a[title="Websites & Snippets"]',
      content: (
        <div>
          <Paragraph>
            Add websites and install tracking code to start collecting data:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Add Websites:</strong> Register sites with industry categorization</Text>
            <Text>• <strong>Generate Snippets:</strong> Get platform-specific tracking code</Text>
            <Text>• <strong>Status Monitoring:</strong> Track installation and data flow</Text>
            <Text>• <strong>Multi-Site Management:</strong> Handle multiple websites</Text>
          </Space>
          <Alert
            message="⚡ Quick Setup"
            description="Get tracking code in under 2 minutes with our guided installation"
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
          
          <Title level={4} style={{ marginTop: 20 }}>Step-by-Step: Creating Your First Snippet</Title>
          <Title level={5}>Step 1: Add Your Website</Title>
          <Space direction="vertical" style={{ marginBottom: 16 }}>
            <Text>1. Click "Websites & Snippets" in the sidebar</Text>
            <Text>2. Click "Add Website" button</Text>
            <Text>3. Enter your website URL (e.g., https://www.example.com)</Text>
            <Text>4. Select industry type for better analytics</Text>
            <Text>5. Click "Save" to add the website</Text>
          </Space>

          <Title level={5}>Step 2: Choose Your Platform</Title>
          <Space direction="vertical" style={{ marginBottom: 16 }}>
            <Text>1. Click "Generate Snippet" next to your website</Text>
            <Text>2. Select your platform: WordPress, Shopify, Wix, Squarespace, etc.</Text>
            <Text>3. Choose snippet type: Standard (recommended) or Legacy</Text>
            <Text>4. Click "Generate" to create the tracking code</Text>
          </Space>

          <Title level={5}>Step 3: Install the Snippet</Title>
          <Paragraph strong>For WordPress Websites:</Paragraph>
          <Space direction="vertical" style={{ marginBottom: 12 }}>
            <Text>• Go to WordPress Admin → Appearance → Theme Editor</Text>
            <Text>• Open footer.php or theme footer file</Text>
            <Text>• Paste the tracking code just before &lt;/body&gt;</Text>
            <Text>• Save changes and refresh your website</Text>
          </Space>

          <Paragraph strong>For Shopify Stores:</Paragraph>
          <Space direction="vertical" style={{ marginBottom: 12 }}>
            <Text>• Go to Online Store → Themes → Edit Code</Text>
            <Text>• Open theme.liquid layout file</Text>
            <Text>• Paste code before &lt;/body&gt; tag</Text>
            <Text>• Save and publish changes</Text>
          </Space>

          <Alert
            message="📋 Supported Platforms"
            description="WordPress, Shopify, Wix, Squarespace, Webflow, Joomla, Drupal, Blogger, Ghost, Magento, Custom HTML"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      )
    },
    {
      title: "Heatmap - Visual User Behaviour",
      description: "Click Heatmap to visualize where users click and interact",
      icon: <HeatMapOutlined style={{ fontSize: 48, color: '#fa541c' }} />,
      highlightSelector: 'a[title="Heatmap"]',
      content: (
        <div>
          <Paragraph>
            See exactly how users interact with your website:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Click Heatmaps:</strong> Hotspots where users click most</Text>
            <Text>• <strong>Move Maps:</strong> Mouse movement patterns</Text>
            <Text>• <strong>Scroll Depth:</strong> How far users scroll down pages</Text>
            <Text>• <strong>Rage Clicks:</strong> Frustration indicators (rapid clicking)</Text>
          </Space>
          <Alert
            message="🔥 Asuma-Style Rendering"
            description="Two-pass rendering creates smooth, professional heatmaps without artifacts"
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
          <div style={{ marginTop: 12, padding: 12, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6 }}>
            <Text strong style={{ color: '#fa8c16' }}>⚡ Enterprise Feature: Advanced heatmap analytics</Text>
          </div>
        </div>
      )
    },
    {
      title: "Form Analytics - Track Conversions",
      description: "Click Form Analytics to monitor form performance",
      icon: <SettingOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      highlightSelector: 'a[title="Form Analytics"]',
      content: (
        <div>
          <Paragraph>
            Monitor how users interact with your forms:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Field Completion:</strong> Track which fields users fill out</Text>
            <Text>• <strong>Drop-off Points:</strong> Identify where users abandon forms</Text>
            <Text>• <strong>Time Analysis:</strong> How long users spend on each field</Text>
            <Text>• <strong>Conversion Funnels:</strong> Step-by-step conversion tracking</Text>
          </Space>
          <Alert
            message="📊 Real-Time Data"
            description="See form interactions as they happen with live updates"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      )
    },
    {
      title: "Session Recordings - Watch User Behaviour",
      description: "Click Session Recordings to replay user sessions",
      icon: <UserOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      highlightSelector: 'a[title="Session Recordings"]',
      content: (
        <div>
          <Paragraph>
            Watch actual user sessions to understand Behaviour:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Video Playback:</strong> See exactly what users did</Text>
            <Text>• <strong>Click Tracking:</strong> Every mouse click and interaction</Text>
            <Text>• <strong>Scroll Behaviour:</strong> How users navigate your pages</Text>
            <Text>• <strong>Time Analysis:</strong> Where users spend time</Text>
          </Space>
          <Alert
            message="🎬 Privacy Compliant"
            description="Sessions are anonymized and comply with privacy regulations"
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      )
    },
    {
      title: "Analytics & Reports - Deep Insights",
      description: "Click Analytics & Reports for detailed performance data",
      icon: <BarChartOutlined style={{ fontSize: 48, color: '#52c41a' }} />,
      highlightSelector: 'a[title="Analytics & Reports"]',
      content: (
        <div>
          <Paragraph>
            Comprehensive analytics and reporting tools:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Visitor Analytics:</strong> Demographics and Behaviour patterns</Text>
            <Text>• <strong>E-commerce Tracking:</strong> Revenue, orders, and conversion rates</Text>
            <Text>• <strong>Custom Reports:</strong> Export data for external analysis</Text>
            <Text>• <strong>Performance Metrics:</strong> Load times, bounce rates, engagement</Text>
          </Space>
          <div style={{ marginTop: 16, padding: 12, background: '#f0f9ff', border: '1px solid #bae7ff', borderRadius: 6 }}>
            <Text strong style={{ color: '#1890ff' }}>📈 Advanced e-commerce analytics included</Text>
          </div>
        </div>
      )
    },
    {
      title: "Channel Comparison - Compare Traffic Sources",
      description: "Click Channel Comparison to analyze different traffic channels",
      icon: <BarChartOutlined style={{ fontSize: 48, color: '#fa8c16' }} />,
      highlightSelector: 'a[title="Channel Comparison"]',
      content: (
        <div>
          <Paragraph>
            Compare and analyze traffic from different sources:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Source Analysis:</strong> Compare organic, paid, social, and direct traffic</Text>
            <Text>• <strong>Performance Metrics:</strong> Conversion rates, bounce rates, session duration</Text>
            <Text>• <strong>Revenue Attribution:</strong> Track revenue by traffic source</Text>
            <Text>• <strong>Trend Analysis:</strong> See how channels perform over time</Text>
          </Space>
          <Alert
            message="📊 Channel Insights"
            description="Identify which marketing channels drive the most valuable traffic and conversions"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
          <div style={{ marginTop: 12, padding: 12, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6 }}>
            <Text strong style={{ color: '#fa8c16' }}>🎯 Optimize your marketing spend with data-driven insights</Text>
          </div>
        </div>
      )
    },
    {
      title: "Team & Roles - Collaborate",
      description: "Click Team & Roles to manage team access",
      icon: <TeamOutlined style={{ fontSize: 48, color: '#722ed1' }} />,
      highlightSelector: 'a[title="Team & Roles"]',
      content: (
        <div>
          <Paragraph>
            Invite team members and control access levels:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Role-Based Access:</strong> Different permissions for team members</Text>
            <Text>• <strong>Invite System:</strong> Send invitations via email</Text>
            <Text>• <strong>Shared Dashboards:</strong> Team-wide analytics access</Text>
            <Text>• <strong>Activity Tracking:</strong> Monitor team usage</Text>
          </Space>
          <Alert
            message="👥 Perfect for Agencies"
            description="Manage client accounts and team collaboration seamlessly"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      )
    },
    {
      title: "Account Settings - Manage Your Account",
      description: "Click Account Settings to configure your profile and preferences",
      icon: <SettingOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      highlightSelector: 'a[title="Account Settings"]',
      content: (
        <div>
          <Paragraph>
            Manage your account, billing, and preferences:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Profile Settings:</strong> Update personal information</Text>
            <Text>• <strong>API Keys:</strong> Generate and manage API access</Text>
            <Text>• <strong>Subscription:</strong> Manage billing and plans</Text>
            <Text>• <strong>Security:</strong> Password and authentication settings</Text>
          </Space>
        </div>
      )
    },
    {
      title: "Support - Get Help",
      description: "Click Support to access help resources and contact us",
      icon: <QuestionCircleOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      highlightSelector: 'a[title="Support"]',
      content: (
        <div>
          <Paragraph>
            Get help and support when you need it:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Help Center:</strong> Browse documentation and guides</Text>
            <Text>• <strong>Contact Support:</strong> Reach out to our team</Text>
            <Text>• <strong>FAQs:</strong> Find answers to common questions</Text>
            <Text>• <strong>Video Tutorials:</strong> Watch step-by-step guides</Text>
          </Space>
          <Alert
            message="💬 We're Here to Help"
            description="Our support team typically responds within 24 hours"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      )
    },
    {
      title: "🎉 You're All Set!",
      description: "Congratulations! You're ready to start analyzing your website data",
      icon: <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />,
      highlightSelector: 'a[title="Dashboard"]',
      content: (
        <div>
          <Paragraph>
            After installing the snippet, verify it's working:
          </Paragraph>
          <Space direction="vertical">
            <Text>• <strong>Visit Your Website:</strong> Generate some activity</Text>
            <Text>• <strong>Check Dashboard:</strong> Look for live visitor data</Text>
            <Text>• <strong>Events Summary:</strong> Should show recent pageviews</Text>
            <Text>• <strong>Real-time Updates:</strong> Data appears within 30 seconds</Text>
          </Space>

          <Title level={4} style={{ marginTop: 20 }}>Troubleshooting:</Title>
          <Space direction="vertical">
            <Text>• <strong>No Data?</strong> Check if code is properly installed</Text>
            <Text>• <strong>Code Issues?</strong> Verify the snippet wasn't modified</Text>
            <Text>• <strong>Browser Issues?</strong> Try incognito mode</Text>
            <Text>• <strong>Need Help?</strong> Contact support or use AI Chat</Text>
          </Space>

          <div style={{ marginTop: 20, padding: 16, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 8, color: 'white' }}>
            <Text strong style={{ color: 'white' }}>
              🎉 Congratulations! You're all set up and ready to analyze your website data.
            </Text>
          </div>
        </div>
      )
    }
  ]), []);

  // Highlight sidebar elements during walkthrough
  useEffect(() => {
    const step = walkthroughSteps[currentStep];
    if (step?.highlightSelector) {
      const element = document.querySelector(step.highlightSelector);
      if (element) {
        element.style.backgroundColor = '#1890ff';
        element.style.color = 'white';
        element.style.borderRadius = '6px';
        element.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.3)';
        setHighlightedElement(element);

        // Scroll into view if needed
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return () => {
      if (highlightedElement) {
        highlightedElement.style.backgroundColor = '';
        highlightedElement.style.color = '';
        highlightedElement.style.borderRadius = '';
        highlightedElement.style.boxShadow = '';
      }
    };
  }, [currentStep, highlightedElement, walkthroughSteps]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (highlightedElement) {
        highlightedElement.style.backgroundColor = '';
        highlightedElement.style.color = '';
        highlightedElement.style.borderRadius = '';
        highlightedElement.style.boxShadow = '';
      }
    };
  }, [highlightedElement]);

  const handleNext = () => {
    if (currentStep < walkthroughSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  const handleSkip = () => {
    handleComplete();
  };

  const progress = ((currentStep + 1) / walkthroughSteps.length) * 100;

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleSkip}
      footer={null}
      width={620}
      centered
      maskClosable={false}
      closable={false}
      bodyStyle={{ padding: 0 }}
      className="walkthrough-modal"
    >
      <div style={{ position: 'relative' }}>
        {/* Progress bar */}
        <div style={{ padding: '16px 24px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text type="secondary">Step {currentStep + 1} of {walkthroughSteps.length}</Text>
            <Button type="text" onClick={handleSkip} size="small">Skip Tour</Button>
          </div>
          <div
            style={{
              width: '100%',
              height: 4,
              background: 'linear-gradient(90deg, #1890ff 0%, #36cfc9 100%)',
              borderRadius: 2,
              transform: `scaleX(${progress / 100})`,
              transformOrigin: 'left',
              transition: 'transform 0.3s ease'
            }}
          />
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Content */}
        <div style={{ padding: '0 24px 24px', maxHeight: '500px', overflowY: 'auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div className="walkthrough-step-icon">
              {walkthroughSteps[currentStep].icon}
            </div>
            <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
              {walkthroughSteps[currentStep].title}
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              {walkthroughSteps[currentStep].description}
            </Text>
          </div>

          <Card
            className="walkthrough-card"
            style={{
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: 24
            }}
            bodyStyle={{ padding: 20 }}
          >
            {walkthroughSteps[currentStep].content}
          </Card>
        </div>

        {/* Navigation buttons - always visible */}
        <div style={{ 
          padding: '0 24px 24px', 
          borderTop: '1px solid #f0f0f0',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            icon={<LeftOutlined />}
          >
            Previous
          </Button>

          <Button
            type="primary"
            onClick={handleNext}
            icon={currentStep === walkthroughSteps.length - 1 ? <CheckCircleOutlined /> : <RightOutlined />}
            iconPosition="end"
          >
            {currentStep === walkthroughSteps.length - 1 ? 'Get Started' : 'Next'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AppWalkthrough;
